import type { Point, SceneNode } from "../scene/model";
import { bounds } from "./geometry";
import { transformPoint } from "../geometry/affine";
import { nodeMatrix } from "./transforms";
import { edgeOutline, zeroEdges } from "../geometry/edge-warp";
import { localOutline } from "./stroke";
import { shapeStyle, strokeRadius } from "./appearance";
import { handAmplitude } from "./hand-stroke";
import { arrowPoints, pathPoints, vectorPath } from "./vector-path";
import { characterStyle, textData } from "./text";
import type { GradientPaint } from "./gradient";
import { clippingBase } from "./groups";

export type BoundaryInterval = [number, number];
export interface BoundaryEdge {
  start: Point;
  end: Point;
  intervals: BoundaryInterval[];
}
const painted = (alpha: number | undefined, gradient?: GradientPaint | null) =>
  (alpha ?? 1) > 0 && (!gradient || gradient.stops.some((s) => s.alpha > 0));

/** Geometry-only guide, as in the original: image alpha padding, text glyph holes
 * and raster effects do not change the footprint. No image decoding/readback. */
function mayCoverEdge(
  left: number,
  top: number,
  right: number,
  bottom: number,
  width: number,
  height: number,
) {
  return !(
    left > width ||
    right < 0 ||
    top > height ||
    bottom < 0 ||
    (left > 0 && right < width && top > 0 && bottom < height)
  );
}
function footprint(node: SceneNode, width: number, height: number): Point[][] {
  if (node.appearance.visible === false || node.appearance.opacity === 0)
    return [];
  const w = node.geometry.width!,
    h = node.geometry.height!,
    style = shapeStyle(node),
    fill =
      style.fillEnabled &&
      painted(
        Number(node.appearance.fillAlpha ?? 1),
        node.appearance.fillGradient as GradientPaint | undefined,
      ),
    stroke =
      strokeRadius(node) > 0 &&
      painted(
        Number(node.appearance.strokeAlpha ?? 1),
        node.appearance.strokeGradient as GradientPaint | undefined,
      );
  if (node.kind === "text") {
    const t = textData(node);
    if (
      !t.value.trim() ||
      (!(t.strokeWidth > 0 && painted(t.strokeAlpha, t.strokeGradient)) &&
        !t.value.split("").some((_, i) => {
          const s = characterStyle(t, i);
          return painted(s.colorAlpha, s.colorGradient);
        }))
    )
      return [];
  } else if (node.kind !== "image" && !fill && !stroke) return [];
  const matrix = nodeMatrix(node),
    project = (p: Point) => transformPoint(p, matrix);
  if (node.geometry.warpCorners)
    return [
      edgeOutline(
        node.geometry.warpCorners,
        node.geometry.warpEdges ?? zeroEdges(),
        64,
      ).map(project),
    ];
  if (node.kind === "image" || node.kind === "text" || node.geometry.svgPath)
    return [
      [
        { x: -0.5, y: -0.5 },
        { x: 0.5, y: -0.5 },
        { x: 0.5, y: 0.5 },
        { x: -0.5, y: 0.5 },
      ].map(project),
    ];
  const path = node.kind === "path" ? vectorPath(node) : null;
  const points = path
    ? pathPoints(node, 48).map((p) => ({ x: p.x * w, y: p.y * h }))
    : localOutline(node, 256);
  const polygons: Point[][] = [];
  const localProject = (p: Point) => project({ x: p.x / w, y: p.y / h });
  const world = points.map(localProject);
  if (fill && (!path || path.closed)) polygons.push(world);
  // The guide follows the stroke envelope. Dash gaps are intentionally ignored,
  // like transparent padding in images; they must not make the guide flicker.
  if (stroke) {
    const radius =
        strokeRadius(node) +
        (style.lineStyle === "hand" ? handAmplitude(style.strokeWidth) : 0),
      closed = !path || path.closed,
      guardRadius = radius * (style.lineCap === "square" ? Math.SQRT2 : 1),
      rx = guardRadius * Math.hypot(matrix[0] / w, matrix[2] / h),
      ry = guardRadius * Math.hypot(matrix[1] / w, matrix[3] / h);
    // Only expand stroke pieces near an edge. A long pen line mostly inside the
    // page must not allocate round-join polygons for every sampled curve point.
    const nearEdge = (a: Point, b = a) =>
      mayCoverEdge(
        Math.min(a.x, b.x) - rx,
        Math.min(a.y, b.y) - ry,
        Math.max(a.x, b.x) + rx,
        Math.max(a.y, b.y) + ry,
        width,
        height,
      );
    for (let i = 0; i < points.length - (closed ? 0 : 1); i++) {
      if (!nearEdge(world[i]!, world[(i + 1) % world.length]!)) continue;
      const a = points[i]!,
        b = points[(i + 1) % points.length]!,
        dx = b.x - a.x,
        dy = b.y - a.y,
        length = Math.hypot(dx, dy);
      if (!length) continue;
      const nx = (-dy / length) * radius,
        ny = (dx / length) * radius;
      const first =
          !closed && style.lineCap === "square" && i === 0
            ? radius / length
            : 0,
        last =
          !closed && style.lineCap === "square" && i === points.length - 2
            ? radius / length
            : 0;
      polygons.push(
        [
          { x: a.x + nx - dx * first, y: a.y + ny - dy * first },
          { x: b.x + nx + dx * last, y: b.y + ny + dy * last },
          { x: b.x - nx + dx * last, y: b.y - ny + dy * last },
          { x: a.x - nx - dx * first, y: a.y - ny - dy * first },
        ].map(localProject),
      );
    }
    points.forEach((p, i) => {
      if (!nearEdge(world[i]!)) return;
      if (
        !closed &&
        style.lineCap !== "round" &&
        (i === 0 || i === points.length - 1)
      )
        return;
      polygons.push(
        Array.from({ length: 24 }, (_, j) => ({
          x: p.x + radius * Math.cos((j * Math.PI) / 12),
          y: p.y + radius * Math.sin((j * Math.PI) / 12),
        })).map(localProject),
      );
    });
    const arrow = path?.arrow ? arrowPoints(node) : [];
    if (arrow.length) polygons.push(arrow.map(project));
  }
  return polygons;
}

function contains(p: Point, polygon: readonly Point[]) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[j]!,
      b = polygon[i]!,
      dx = b.x - a.x,
      dy = b.y - a.y;
    if (
      Math.abs((p.x - a.x) * dy - (p.y - a.y) * dx) < 1e-8 &&
      p.x >= Math.min(a.x, b.x) - 1e-8 &&
      p.x <= Math.max(a.x, b.x) + 1e-8 &&
      p.y >= Math.min(a.y, b.y) - 1e-8 &&
      p.y <= Math.max(a.y, b.y) + 1e-8
    )
      return true;
    if (
      a.y > p.y !== b.y > p.y &&
      p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x
    )
      inside = !inside;
  }
  return inside;
}
export function mergeBoundaryIntervals(
  intervals: BoundaryInterval[],
): BoundaryInterval[] {
  const result: BoundaryInterval[] = [];
  for (const v of intervals.sort((a, b) => a[0] - b[0])) {
    const last = result.at(-1);
    if (last && v[0] <= last[1] + 1e-7) last[1] = Math.max(last[1], v[1]);
    else result.push([...v]);
  }
  return result;
}
function covered(
  start: Point,
  end: Point,
  polygons: Point[][],
): BoundaryInterval[] {
  const dx = end.x - start.x,
    dy = end.y - start.y,
    length = Math.hypot(dx, dy),
    intervals: BoundaryInterval[] = [];
  if (!length) return intervals;
  const at = (t: number) => ({ x: start.x + dx * t, y: start.y + dy * t });
  for (const polygon of polygons) {
    const cuts = [0, 1];
    polygon.forEach((a, i) => {
      const b = polygon[(i + 1) % polygon.length]!,
        sx = b.x - a.x,
        sy = b.y - a.y,
        ax = a.x - start.x,
        ay = a.y - start.y,
        den = dx * sy - dy * sx;
      if (Math.abs(den) < 1e-8) {
        if (Math.abs(ax * dy - ay * dx) < 1e-6)
          for (const p of [a, b])
            cuts.push(
              Math.max(
                0,
                Math.min(
                  1,
                  ((p.x - start.x) * dx + (p.y - start.y) * dy) /
                    (length * length),
                ),
              ),
            );
      } else {
        const t = (ax * sy - ay * sx) / den,
          u = (ax * dy - ay * dx) / den;
        if (t >= 0 && t <= 1 && u >= 0 && u <= 1) cuts.push(t);
      }
    });
    cuts.sort((a, b) => a - b);
    for (let i = 1; i < cuts.length; i++) {
      const a = cuts[i - 1]!,
        b = cuts[i]!;
      if (b - a < 1e-7) continue;
      const p = at((a + b) / 2),
        inset = Math.max(1e-5, length * 1e-7);
      if (
        contains(p, polygon) &&
        contains(
          { x: p.x - (dy / length) * inset, y: p.y + (dx / length) * inset },
          polygon,
        )
      )
        intervals.push([a, b]);
    }
  }
  return mergeBoundaryIntervals(intervals);
}

/** Immutable nodes are cached across selection, zoom and pan; moving one object
 * never recomputes the footprints of every other object in a dense scene. */
export class PageBoundary {
  private cache = new WeakMap<
    SceneNode,
    { width: number; height: number; edges: BoundaryInterval[][] }
  >();
  coverage(
    nodes: readonly SceneNode[],
    width: number,
    height: number,
  ): BoundaryEdge[] {
    const corners = [
      { x: 0, y: 0 },
      { x: width, y: 0 },
      { x: width, y: height },
      { x: 0, y: height },
    ];
    const edges = corners.map((start, i) => ({
      start,
      end: corners[(i + 1) % 4]!,
      intervals: [] as BoundaryInterval[],
    }));
    const own = (n: SceneNode) => {
      let item = this.cache.get(n);
      if (item?.width === width && item.height === height) return item.edges;
      const b = bounds(n);
      if (n.kind === "path" && shapeStyle(n).lineCap === "square") {
        const matrix = nodeMatrix(n),
          extra = strokeRadius(n) * (Math.SQRT2 - 1),
          dx =
            extra *
            Math.hypot(
              matrix[0] / n.geometry.width!,
              matrix[2] / n.geometry.height!,
            ),
          dy =
            extra *
            Math.hypot(
              matrix[1] / n.geometry.width!,
              matrix[3] / n.geometry.height!,
            );
        b.x -= dx;
        b.y -= dy;
        b.width += 2 * dx;
        b.height += 2 * dy;
      }
      const right = b.x + b.width,
        bottom = b.y + b.height;
      const candidate = mayCoverEdge(b.x, b.y, right, bottom, width, height);
      const polygons = candidate ? footprint(n, width, height) : [];
      item = {
        width,
        height,
        edges: edges.map((e) => covered(e.start, e.end, polygons)),
      };
      this.cache.set(n, item);
      return item.edges;
    };
    for (const n of nodes) {
      let intervals = own(n);
      if (n.layer.clipToBelow) {
        const base = clippingBase(nodes, n);
        if (!base) continue;
        const mask = own(base);
        intervals = intervals.map((list, i) =>
          list.flatMap((a) =>
            mask[i]!.flatMap((b) => {
              const from = Math.max(a[0], b[0]),
                to = Math.min(a[1], b[1]);
              return to > from ? [[from, to] as BoundaryInterval] : [];
            }),
          ),
        );
      }
      edges.forEach((e, i) => e.intervals.push(...intervals[i]!));
    }
    return edges.map((e) => ({
      ...e,
      intervals: mergeBoundaryIntervals(e.intervals),
    }));
  }
}
